import {
  Box,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';

import { useQuery } from 'urql';

import {
  clearImpersonation,
  IMPERSONATION_PRESETS,
  type ImpersonationPreset,
  selectImpersonatedUser,
  selectImpersonatedUserId,
  selectImpersonationOverrides,
  selectIsImpersonating,
  setOverrides,
  startImpersonation,
  type UserOverrides,
  useGetLoggedInUser,
  useImpersonationSelector,
} from '@revelio/auth';
import { useTourStore } from '@revelio/core';
import {
  type GetAllClientsQuery,
  GetClientDetailsDocument,
  type GetClientDetailsQuery,
  type GetUserDetailsQuery,
  Role,
  type Tab,
  TrialType,
  USER_DETAILS_QUERY,
  type User,
} from '@revelio/data-access';

import { AllClientsQuery } from '../../userOperations';
import {
  getReportOptions,
  mapServerReportResponseToSelectOptions,
  mapServerResponseToSelectOptions,
  pipelineOptions,
  tabOptions,
} from '../../utils/helpers';
import { ImpersonationClientGroupQuickSelect } from './ImpersonationClientGroupQuickSelect';
import { ImpersonationClientOverridesForm } from './ImpersonationClientOverridesForm';
import { ImpersonationModalActions } from './ImpersonationModalActions';
import {
  type ImpersonationMode,
  ImpersonationModePicker,
} from './ImpersonationModePicker';
import { ImpersonationPresetCards } from './ImpersonationPresetCards';
import { ImpersonationSearchCard } from './ImpersonationSearchCard';
import { ImpersonationUserOverridesForm } from './ImpersonationUserOverridesForm';
import {
  buildClientGroupSeedOverrides,
  buildOverridesFromClient,
  buildOverridesFromUser,
  DEFAULT_CUSTOM_OVERRIDES,
  DEFAULT_TRIAL_EXPIRATION_DAYS,
  getActiveStatusDetails,
  getTrialExpirationDate,
  getTrialStatusDetails,
  isTrialExpiredForUser,
  mergeOverridesWithDefaults,
  resolvePresetName,
  type SelectOption,
} from './impersonationModal.utils';

interface ImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const resolveInitialMode = ({
  isImpersonating,
  hasImpersonatedUser,
  hasOverrides,
  presetMatches,
}: {
  isImpersonating: boolean;
  hasImpersonatedUser: boolean;
  hasOverrides: boolean;
  presetMatches: boolean;
}): ImpersonationMode => {
  if (isImpersonating && hasImpersonatedUser) return 'search';
  if (hasOverrides && presetMatches) return 'preset';
  if (hasOverrides) return 'custom';
  return 'preset';
};

export function ImpersonationModal({
  isOpen,
  onClose,
}: ImpersonationModalProps) {
  const { realLoggedInUser } = useGetLoggedInUser();
  const setIsTour = useTourStore((s) => s.setIsTour);
  const setStepIndex = useTourStore((s) => s.setStepIndex);
  const setIsTourComplete = useTourStore((s) => s.setIsTourComplete);
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const impersonatedUserId = useImpersonationSelector(selectImpersonatedUserId);
  const impersonatedUser = useImpersonationSelector(selectImpersonatedUser);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);

  const [customOverrides, setCustomOverrides] = useState<UserOverrides>({
    ...DEFAULT_CUSTOM_OVERRIDES,
  });
  const [selectedPresetName, setSelectedPresetName] = useState<string | null>(
    null
  );
  const [pendingUser, setPendingUser] = useState<Partial<User> | null>(null);
  // The client group the admin has actively picked as a quick view. Kept
  // separate from customOverrides.clientName, which also carries the admin's
  // own client as a baseline and must not read as an active selection.
  const [selectedClientGroup, setSelectedClientGroup] = useState<string | null>(
    null
  );
  // Set to a client group name while its details load, then cleared once its
  // settings have been seeded into customOverrides (see the seed effect).
  const [pendingClientGroupSeed, setPendingClientGroupSeed] = useState<
    string | null
  >(null);
  const [mode, setMode] = useState<ImpersonationMode>('preset');

  const [{ data: clientData }] = useQuery<GetAllClientsQuery>({
    query: AllClientsQuery,
    pause: !isOpen,
  });
  const shouldFetchImpersonatedUser =
    isImpersonating && Boolean(impersonatedUserId);
  const [{ data: impersonatedUserData }] = useQuery<GetUserDetailsQuery>({
    query: USER_DETAILS_QUERY,
    variables: {
      ids: impersonatedUserId ? [impersonatedUserId] : [],
    },
    pause: !shouldFetchImpersonatedUser,
  });

  const clientSelectOptions = useMemo(() => {
    const clientOptions =
      clientData?.clients
        ?.map((client) => client?.client_name)
        .filter((clientName): clientName is string => Boolean(clientName))
        .sort((a, b) => a.localeCompare(b)) ?? [];

    return clientOptions.map((clientName) => ({
      label: clientName,
      value: clientName,
    }));
  }, [clientData]);

  const trialTypeOptions = useMemo<SelectOption<TrialType>[]>(
    () => [
      {
        value: TrialType.ONE_YEAR_DATA_LAG,
        label: 'CST Trial (1 Year Data Lag)',
      },
      {
        value: TrialType.NO_DATA_LAG,
        label: 'Free Trial (No Data Lag)',
      },
      {
        value: TrialType.NO_DATA_LAG_WITH_TD_REVEAL,
        label: 'Free Trial with Promo (Manual Unblur)',
      },
    ],
    []
  );

  const roleOptions = useMemo<SelectOption<Role>[]>(
    () => [
      { value: Role.ClientUser, label: 'Client User' },
      { value: Role.ClientAdmin, label: 'Client Admin' },
      { value: Role.RevelioAdmin, label: 'Revelio Admin' },
      { value: Role.SuperAdmin, label: 'Super Admin' },
    ],
    []
  );

  const tabSelectOptions = useMemo<SelectOption<Tab>[]>(
    () =>
      tabOptions.map((option) => ({
        label: option.label,
        value: option.value,
      })),
    []
  );

  const resolvedImpersonatedUser =
    impersonatedUserData?.users?.[0] ?? impersonatedUser;
  const selectedSearchUser = pendingUser ?? resolvedImpersonatedUser;
  const isSelectedUserActive = selectedSearchUser?.active;
  const isSelectedUserTrialUser = selectedSearchUser?.live === false;
  const isSelectedUserTrialExpired = isTrialExpiredForUser(selectedSearchUser);
  const activeStatus = getActiveStatusDetails(isSelectedUserActive);
  const trialStatus = getTrialStatusDetails(
    isSelectedUserTrialUser,
    isSelectedUserTrialExpired
  );

  const baseClientName =
    isImpersonating && resolvedImpersonatedUser
      ? resolvedImpersonatedUser.client_name
      : realLoggedInUser?.client_name;
  const selectedClientName = customOverrides.clientName ?? baseClientName;
  const realClientName = realLoggedInUser?.client_name;
  const [{ data: baseClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: baseClientName ? [baseClientName] : [],
    },
    pause: !isOpen || !baseClientName,
  });
  const shouldFetchSelectedClient = Boolean(
    isOpen && selectedClientName && selectedClientName !== baseClientName
  );
  const [{ data: selectedClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: selectedClientName ? [selectedClientName] : [],
    },
    pause: !shouldFetchSelectedClient,
  });
  const shouldFetchRealClient = Boolean(
    isOpen && realClientName && realClientName !== baseClientName
  );
  const [{ data: realClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: realClientName ? [realClientName] : [],
    },
    pause: !shouldFetchRealClient,
  });

  const baseClient = baseClientData?.clients?.[0];
  const selectedClient = selectedClientData?.clients?.[0] ?? baseClient;
  const realClient =
    realClientName === baseClientName
      ? baseClient
      : realClientData?.clients?.[0];
  const reportOptions = useMemo(() => {
    const reportsConfiguration = selectedClient?.reports_configuration;
    if (!reportsConfiguration) {
      return [];
    }

    return getReportOptions({ reports_configuration: reportsConfiguration });
  }, [selectedClient?.reports_configuration]);

  const selectedTrialTypeOption = trialTypeOptions.find(
    (option) => option.value === customOverrides.trialType
  );
  const selectedRoleOption = roleOptions.find(
    (option) => option.value === customOverrides.role
  );
  const selectedClientOption = clientSelectOptions.find(
    (option) => option.value === customOverrides.clientName
  );
  const selectedQuickClientGroupOption = selectedClientGroup
    ? (clientSelectOptions.find(
        (option) => option.value === selectedClientGroup
      ) ?? { label: selectedClientGroup, value: selectedClientGroup })
    : undefined;
  const selectedTabOptions = tabSelectOptions.filter((option) =>
    customOverrides.tabs?.includes(option.value)
  );
  const selectedPipelineOptions = mapServerResponseToSelectOptions(
    customOverrides.pipelineTypes,
    customOverrides.postingSources,
    pipelineOptions
  );
  const selectedReportOptions = mapServerReportResponseToSelectOptions(
    customOverrides.reportTypes ?? [],
    reportOptions
  );

  const realUserOverrides = useMemo<UserOverrides>(() => {
    if (realLoggedInUser?.id) {
      return mergeOverridesWithDefaults({
        ...buildOverridesFromUser(realLoggedInUser),
        ...buildOverridesFromClient(realClient),
      });
    }

    return { ...DEFAULT_CUSTOM_OVERRIDES };
  }, [realLoggedInUser, realClient]);
  const baseUserOverrides = useMemo<UserOverrides>(() => {
    if (isImpersonating && resolvedImpersonatedUser) {
      return mergeOverridesWithDefaults({
        ...buildOverridesFromUser(resolvedImpersonatedUser),
        ...buildOverridesFromClient(baseClient),
      });
    }

    return realUserOverrides;
  }, [
    isImpersonating,
    resolvedImpersonatedUser,
    baseClient,
    realUserOverrides,
  ]);
  const buildOverridesDiff = (
    nextOverrides: UserOverrides,
    baseOverrides: UserOverrides
  ): UserOverrides | null => {
    const diff: UserOverrides = {};

    if (nextOverrides.isTrialUser !== baseOverrides.isTrialUser) {
      diff.isTrialUser = nextOverrides.isTrialUser;
    }

    if (nextOverrides.live !== baseOverrides.live) {
      diff.live = nextOverrides.live;
    }

    if (
      nextOverrides.trialType !== undefined &&
      nextOverrides.trialType !== baseOverrides.trialType
    ) {
      diff.trialType = nextOverrides.trialType;
    }

    if (
      nextOverrides.expirationDate !== undefined &&
      nextOverrides.expirationDate !== baseOverrides.expirationDate
    ) {
      diff.expirationDate = nextOverrides.expirationDate;
    }

    if (
      nextOverrides.tourCompleted !== undefined &&
      nextOverrides.tourCompleted !== baseOverrides.tourCompleted
    ) {
      diff.tourCompleted = nextOverrides.tourCompleted;
    }

    if (
      nextOverrides.stepsCompleted !== undefined &&
      nextOverrides.stepsCompleted !== baseOverrides.stepsCompleted
    ) {
      diff.stepsCompleted = nextOverrides.stepsCompleted;
    }

    if (
      nextOverrides.tourInterestTalentAcquisition !== undefined &&
      nextOverrides.tourInterestTalentAcquisition !==
        baseOverrides.tourInterestTalentAcquisition
    ) {
      diff.tourInterestTalentAcquisition =
        nextOverrides.tourInterestTalentAcquisition;
    }

    if (
      nextOverrides.tourInterestCustomData !== undefined &&
      nextOverrides.tourInterestCustomData !==
        baseOverrides.tourInterestCustomData
    ) {
      diff.tourInterestCustomData = nextOverrides.tourInterestCustomData;
    }

    if (
      nextOverrides.role !== undefined &&
      nextOverrides.role !== baseOverrides.role
    ) {
      diff.role = nextOverrides.role;
    }

    if (
      nextOverrides.clientName !== undefined &&
      nextOverrides.clientName !== baseOverrides.clientName
    ) {
      diff.clientName = nextOverrides.clientName;
    }

    const baseTabs = baseOverrides.tabs ?? [];
    const nextTabs = nextOverrides.tabs ?? [];
    const tabsMatch =
      nextOverrides.tabs === undefined ||
      (nextTabs.length === baseTabs.length &&
        nextTabs.every((tab) => baseTabs.includes(tab)));

    if (nextOverrides.tabs !== undefined && !tabsMatch) {
      diff.tabs = nextOverrides.tabs;
    }

    if (nextOverrides.linkupPostings !== baseOverrides.linkupPostings) {
      diff.linkupPostings = nextOverrides.linkupPostings;
    }

    const basePipelineTypes = baseOverrides.pipelineTypes ?? [];
    const nextPipelineTypes = nextOverrides.pipelineTypes ?? [];
    const pipelineTypesMatch =
      nextOverrides.pipelineTypes === undefined ||
      (nextPipelineTypes.length === basePipelineTypes.length &&
        nextPipelineTypes.every((type) => basePipelineTypes.includes(type)));

    if (nextOverrides.pipelineTypes !== undefined && !pipelineTypesMatch) {
      diff.pipelineTypes = nextOverrides.pipelineTypes;
    }

    const basePostingSources = baseOverrides.postingSources ?? [];
    const nextPostingSources = nextOverrides.postingSources ?? [];
    const postingSourcesMatch =
      nextOverrides.postingSources === undefined ||
      (nextPostingSources.length === basePostingSources.length &&
        nextPostingSources.every((source) =>
          basePostingSources.includes(source)
        ));

    if (nextOverrides.postingSources !== undefined && !postingSourcesMatch) {
      diff.postingSources = nextOverrides.postingSources;
    }

    const baseReportTypes = baseOverrides.reportTypes ?? [];
    const nextReportTypes = nextOverrides.reportTypes ?? [];
    const reportTypesMatch =
      nextOverrides.reportTypes === undefined ||
      (nextReportTypes.length === baseReportTypes.length &&
        nextReportTypes.every((type) => baseReportTypes.includes(type)));

    if (nextOverrides.reportTypes !== undefined && !reportTypesMatch) {
      diff.reportTypes = nextOverrides.reportTypes;
    }

    return Object.keys(diff).length > 0 ? diff : null;
  };
  const applyPartialOverridesReset = (nextOverrides: UserOverrides) => {
    if (!overrides) {
      return;
    }

    const nextDiff = buildOverridesDiff(nextOverrides, realUserOverrides);
    if (!nextDiff) {
      clearImpersonation();
      setSelectedPresetName(null);
      return;
    }

    setOverrides(nextDiff);
  };
  const handleClose = () => {
    onClose();
  };

  const handlePresetClick = (preset: ImpersonationPreset) => {
    const nextOverrides = mergeOverridesWithDefaults({
      ...preset.overrides,
      expirationDate: preset.trialDurationDays
        ? getTrialExpirationDate(preset.trialDurationDays)
        : undefined,
    });
    setSelectedPresetName(preset.name);
    setSelectedClientGroup(null);
    setPendingClientGroupSeed(null);
    setCustomOverrides(nextOverrides);
  };

  // Shared by the preset-panel quick-select and the custom-overrides Client
  // Group dropdown: set the name now, then seed the group's settings once its
  // details load (see the seed effect below). Presets and client groups are
  // mutually exclusive quick actions, so picking one clears the other.
  const handleSelectClientGroup = (clientName: string | undefined) => {
    if (!clientName) {
      setSelectedClientGroup(null);
      setPendingClientGroupSeed(null);
      setCustomOverrides((prev) => ({ ...prev, clientName: undefined }));
      return;
    }

    setSelectedPresetName(null);
    setSelectedClientGroup(clientName);
    setPendingClientGroupSeed(clientName);
    setCustomOverrides((prev) => ({ ...prev, clientName }));
  };

  const buildOverridesForUser = (user: Partial<User>): UserOverrides => {
    const userOverrides = buildOverridesFromUser(user);
    return mergeOverridesWithDefaults(userOverrides);
  };

  const handleSelectSearchUser = (user: Partial<User>) => {
    setPendingUser(user);
    setCustomOverrides(buildOverridesForUser(user));
    setSelectedPresetName(null);
  };

  const handleClearSearchSelection = () => {
    setPendingUser(null);
    setCustomOverrides({ ...realUserOverrides });
  };

  const handleEditOverrides = () => {
    setMode('custom');
  };

  const handleApply = () => {
    // Search mode is the only path that sets impersonatedUserId (user-scoped
    // reads follow it), so it returns early: startImpersonation and setOverrides
    // are mutually exclusive, and falling through to setOverrides clears the id.
    if (mode === 'search') {
      const searchUser = pendingUser ?? resolvedImpersonatedUser;
      if (searchUser) {
        startImpersonation(searchUser);
      }
      setPendingUser(null);
      handleClose();
      return;
    }

    const overridesToApply: UserOverrides = {};

    if (customOverrides.isTrialUser !== undefined) {
      overridesToApply.isTrialUser = customOverrides.isTrialUser;
      overridesToApply.live = !customOverrides.isTrialUser;
    }

    if (customOverrides.trialType) {
      overridesToApply.trialType = customOverrides.trialType;
    }

    if (customOverrides.isTrialUser && !customOverrides.expirationDate) {
      overridesToApply.expirationDate = getTrialExpirationDate(
        DEFAULT_TRIAL_EXPIRATION_DAYS
      );
    }

    if (customOverrides.expirationDate) {
      overridesToApply.expirationDate = customOverrides.expirationDate;
    }

    if (customOverrides.tourCompleted !== undefined) {
      overridesToApply.tourCompleted = customOverrides.tourCompleted;
    }

    if (customOverrides.stepsCompleted !== undefined) {
      overridesToApply.stepsCompleted = customOverrides.stepsCompleted;
    }

    if (customOverrides.tourInterestTalentAcquisition !== undefined) {
      overridesToApply.tourInterestTalentAcquisition =
        customOverrides.tourInterestTalentAcquisition;
    }

    if (customOverrides.tourInterestCustomData !== undefined) {
      overridesToApply.tourInterestCustomData =
        customOverrides.tourInterestCustomData;
    }

    if (customOverrides.clientName) {
      overridesToApply.clientName = customOverrides.clientName;
    }

    if (customOverrides.role) {
      overridesToApply.role = customOverrides.role;
    }

    if (customOverrides.tabs !== undefined) {
      overridesToApply.tabs = customOverrides.tabs;
    }

    if (customOverrides.linkupPostings !== undefined) {
      overridesToApply.linkupPostings = customOverrides.linkupPostings;
    }
    if (customOverrides.pipelineTypes !== undefined) {
      overridesToApply.pipelineTypes = customOverrides.pipelineTypes;
    }
    if (customOverrides.postingSources !== undefined) {
      overridesToApply.postingSources = customOverrides.postingSources;
    }
    if (customOverrides.reportTypes !== undefined) {
      overridesToApply.reportTypes = customOverrides.reportTypes;
    }

    const shouldResetTourState =
      overridesToApply.tourCompleted === false ||
      overridesToApply.tourInterestTalentAcquisition !== undefined ||
      overridesToApply.tourInterestCustomData !== undefined;
    if (shouldResetTourState) {
      setIsTour(false);
      setStepIndex(0);
      setIsTourComplete(false);
    }

    setOverrides(overridesToApply);
    setPendingUser(null);
    handleClose();
  };

  const handleResetUserOverrides = () => {
    const nextOverrides: UserOverrides = {
      ...customOverrides,
      isTrialUser: realUserOverrides.isTrialUser,
      live: realUserOverrides.live,
      trialType: realUserOverrides.trialType,
      expirationDate: realUserOverrides.expirationDate,
      tourCompleted: realUserOverrides.tourCompleted,
      stepsCompleted: realUserOverrides.stepsCompleted,
      tourInterestTalentAcquisition:
        realUserOverrides.tourInterestTalentAcquisition,
      tourInterestCustomData: realUserOverrides.tourInterestCustomData,
      role: realUserOverrides.role,
    };

    setCustomOverrides(nextOverrides);
    applyPartialOverridesReset(nextOverrides);
  };

  const handleResetClientOverrides = () => {
    const nextOverrides: UserOverrides = {
      ...customOverrides,
      clientName: realUserOverrides.clientName,
      tabs: realUserOverrides.tabs,
      linkupPostings: realUserOverrides.linkupPostings,
      pipelineTypes: realUserOverrides.pipelineTypes,
      postingSources: realUserOverrides.postingSources,
      reportTypes: realUserOverrides.reportTypes,
    };

    setCustomOverrides(nextOverrides);
    applyPartialOverridesReset(nextOverrides);
  };

  const handleResetImpersonation = () => {
    clearImpersonation();
    setSelectedPresetName(null);
    setSelectedClientGroup(null);
    setPendingClientGroupSeed(null);
    setPendingUser(null);
    setCustomOverrides({ ...realUserOverrides });
    setMode('preset');
  };

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    // Initialize ONLY on the closed→open transition. The deps below also
    // change identity when the modal-open queries (GetClientDetails /
    // GetUserDetails) resolve, and re-running the body then would stomp the
    // admin's in-progress interaction — reset the picked mode, wipe a pending
    // search selection and any form edits. On a slow connection the panel a
    // user just switched to would flip back to presets. The client-level
    // fields those queries would add to the seed (pipelineTypes /
    // postingSources / reportTypes) stay undefined in a pre-query seed, which
    // buildOverridesDiff skips, so seeding at open cannot produce a spurious
    // diff at apply time.
    if (!justOpened) return;

    // A picked client group is a within-session quick action, not something we
    // restore from persisted overrides, so clear it whenever the modal (re)opens.
    setSelectedClientGroup(null);
    setPendingClientGroupSeed(null);

    if (overrides) {
      const nextOverrides = {
        ...baseUserOverrides,
        ...overrides,
      };
      setCustomOverrides(nextOverrides);
      const presetMatch = resolvePresetName(overrides);
      setSelectedPresetName(presetMatch);
      setPendingUser(null);
      setMode(
        resolveInitialMode({
          isImpersonating,
          hasImpersonatedUser: Boolean(resolvedImpersonatedUser),
          hasOverrides: true,
          presetMatches: presetMatch !== null,
        })
      );
      return;
    }

    if (isImpersonating && resolvedImpersonatedUser) {
      setCustomOverrides({ ...baseUserOverrides });
      setSelectedPresetName(null);
      setPendingUser(null);
      setMode('search');
      return;
    }

    setSelectedPresetName(null);
    setPendingUser(null);
    setCustomOverrides({ ...realUserOverrides });
    setMode('preset');
  }, [
    isOpen,
    isImpersonating,
    overrides,
    realUserOverrides,
    baseUserOverrides,
    resolvedImpersonatedUser,
  ]);

  // Seed the picked client group's settings into custom overrides once that
  // group's details have loaded (selectedClient resolves asynchronously).
  useEffect(() => {
    if (!pendingClientGroupSeed) return;
    if (selectedClient?.client_name !== pendingClientGroupSeed) return;

    setCustomOverrides((prev) => ({
      ...prev,
      ...buildClientGroupSeedOverrides(selectedClient),
      clientName: pendingClientGroupSeed,
    }));
    setPendingClientGroupSeed(null);
  }, [pendingClientGroupSeed, selectedClient]);

  // Block Apply while a picked client group is still being seeded: clientName
  // is already set but its tabs/datasets/reports have not merged in yet, so
  // applying now would impersonate with a half-populated override set.
  const applyDisabled =
    (mode === 'search' && !pendingUser && !isImpersonating) ||
    Boolean(pendingClientGroupSeed);

  const summary = (() => {
    if (mode === 'preset') {
      if (selectedPresetName) {
        return `Will apply: ${selectedPresetName} preset`;
      }
      if (selectedClientGroup) {
        return `Will view client group: ${selectedClientGroup}`;
      }
      return 'Pick a preset or client group to continue';
    }
    if (mode === 'search') {
      const searchUser = pendingUser ?? resolvedImpersonatedUser;
      if (!searchUser) return 'Search and select a user to continue';
      const name = searchUser.name || searchUser.email || 'Unknown user';
      return `Will impersonate: ${name}`;
    }
    if (selectedPresetName) {
      return `Custom overrides (based on ${selectedPresetName})`;
    }
    return 'Will apply: custom overrides';
  })();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader>Impersonation Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={5} align="stretch">
            <ImpersonationModePicker mode={mode} onChange={setMode} />

            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              p={5}
            >
              {mode === 'preset' && (
                <VStack spacing={4} align="stretch">
                  <ImpersonationClientGroupQuickSelect
                    clientSelectOptions={clientSelectOptions}
                    selectedClientOption={selectedQuickClientGroupOption}
                    onSelectClientGroup={handleSelectClientGroup}
                  />
                  <Divider />
                  <ImpersonationPresetCards
                    presets={IMPERSONATION_PRESETS}
                    selectedPresetName={selectedPresetName}
                    onSelectPreset={handlePresetClick}
                    onEditOverrides={handleEditOverrides}
                  />
                </VStack>
              )}
              {mode === 'search' && (
                <ImpersonationSearchCard
                  selectedUser={selectedSearchUser}
                  activeStatus={activeStatus}
                  trialStatus={trialStatus}
                  onSelectUser={handleSelectSearchUser}
                  onClearSelection={handleClearSearchSelection}
                  onEditOverrides={handleEditOverrides}
                />
              )}
              {mode === 'custom' && (
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                  <ImpersonationClientOverridesForm
                    customOverrides={customOverrides}
                    setCustomOverrides={setCustomOverrides}
                    onReset={handleResetClientOverrides}
                    clientSelectOptions={clientSelectOptions}
                    selectedClientOption={selectedClientOption}
                    onSelectClientGroup={handleSelectClientGroup}
                    tabSelectOptions={tabSelectOptions}
                    selectedTabOptions={selectedTabOptions}
                    pipelineOptions={pipelineOptions}
                    selectedPipelineOptions={selectedPipelineOptions}
                    reportOptions={reportOptions}
                    selectedReportOptions={selectedReportOptions}
                  />
                  <ImpersonationUserOverridesForm
                    customOverrides={customOverrides}
                    setCustomOverrides={setCustomOverrides}
                    onReset={handleResetUserOverrides}
                    trialTypeOptions={trialTypeOptions}
                    selectedTrialTypeOption={selectedTrialTypeOption}
                    roleOptions={roleOptions}
                    selectedRoleOption={selectedRoleOption}
                  />
                </SimpleGrid>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50">
          <Box w="100%">
            <ImpersonationModalActions
              onResetImpersonation={handleResetImpersonation}
              onApply={handleApply}
              applyDisabled={applyDisabled}
              summary={summary}
            />
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ImpersonationModal;
